import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Button, Select, Form, Input, Modal, Typography, Tooltip, Radio, Table } from 'antd';
import { formItemLayout, tailFormItemLayout, unexpectedError, filter, PictureCell } from '../../common_functions';
import * as userActions from '../../redux/actions/userActions';
import { useDispatch } from 'react-redux';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';

const AdvanceNegativeSearch = ({ visible, onClose, props, selectedNegative, setSelectedNegative, setNegatives, reportType }) => {
  const formNegRef = useRef(null);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [artists, setArtists] = useState([]);
  const [filterNegData, setFilterNegData] = useState([]);

  //Get collection data
  useEffect(() => {
    const getDefaultData = async () => {
      try {
        setLoading(true);
        await getArtists();
      } catch (error) {
        console.error(error);
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    getDefaultData();
  }, [visible === true]);

  const getArtists = async () => {
    const { data: artistFullName } = await props.getItems('Artist', {
      fields: ['id', 'First_Name', 'last_name'],
      sort: '-id',
      limit: -1,
    });
    setArtists(artistFullName);
  };
  const resetNegFilters = async () => {
    formNegRef.current?.resetFields();
    setSelectedNegative(null);
    setNegatives([]);
  };
  const onFinishFilter = async values => {
    try {
      setLoading(true);
      values['collections'] = props?.tabs_permissions?.negatives?.view ? props.collections_permissions.view : [];
      const negFilterObj = {
        fields: ['id,negative.id,negative.obj_name,title,provenance,acquisition,collections.name,collections.id,negative.thumbnail'],
        filters: values,
        sort: 'negative.obj_name',
        limit: '-1',
      };
      const { data } = await props.advancedNegativeFilter(negFilterObj, 'negatives');
      setNegatives(data);
      setFilterNegData(data);
      setLoading(false);
    } catch (error) {
      console.log(error);
      unexpectedError(error);
    } finally {
      setLoading(false);
    }
  };
  const negColumns = [
    {
      title: 'Thumbnail',
      dataIndex: 'thumbnail',
      key: 'thumbnail',
      render: (text, record) =>
        record?.negative?.thumbnail ? (
          <PictureCell
            dataIndexValue={record.negative.thumbnail}
            getFile={() => dispatch(userActions.getFile(record.negative.thumbnail))}
            width={50}
          />
        ) : (
          <span>-</span>
        ),
    },
    {
      title: 'Collection Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.collections.name.localeCompare(b.collections.name),
      ...filter('name'),
      render: (text, record) => record.collections.name,
      onFilter: (value, record) => record.collections.name.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: 'Common Title',
      dataIndex: 'obj_name',
      key: 'obj_name',
      sorter: (a, b) => a.negative.obj_name.localeCompare(b.negative.obj_name),
      ...filter('obj_name'),
      render: (text, record) => record.negative.obj_name,
      onFilter: (value, record) => record.negative.obj_name.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: 'Acquisition id',
      dataIndex: 'acquisition',
      key: 'acquisition',
      ...filter('acquisition'),
      onFilter: (value, record) => record.acquisition.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: 'Select',
      key: 'select',
      render: (text, record) => (
        <span>
          <div className="actionbtngroup">
            <Tooltip placement="top" title="Select Negative">
              <Radio.Group
                name={`radio-group-${record?.negative?.id}`}
                value={selectedNegative}
                onChange={event => {
                  setSelectedNegative(event.target.value);
                }}
              >
                <Radio value={record?.negative?.id} />
              </Radio.Group>
            </Tooltip>
          </div>
        </span>
      ),
    },
  ];

  return (
    <>
      <Modal
        title={`${reportType} Reporting`}
        open={visible}
        onCancel={onClose}
        width={1200}
        footer={[
          <Button
            style={{
              margin: '0 8px',
              backgroundColor: 'lightgrey',
              color: '#333',
              borderColor: '#333',
            }}
            onClick={onClose}
          >
            Close
          </Button>,
          <Button key="submit" type="primary" loading={loading} onClick={onClose}>
            Ok
          </Button>,
        ]}
        confirmLoading={loading}
      >
        <Row gutter={24} align="middle" style={{ marginBottom: 10 }}>
          <Col xs={12}>
            <Typography.Title level={4} style={{ margin: 0 }}>
              Advanced {reportType} Reporting
            </Typography.Title>
          </Col>
        </Row>
        <Form
          ref={formNegRef}
          layout="vertical"
          {...formItemLayout}
          onFinish={onFinishFilter}
          initialValues={{ layout: tailFormItemLayout }}
        >
          <Row gutter={24}>
            <Col xs={24}>
              <Form.Item
                name="keyword"
                label="Keyword"
                rules={[
                  {
                    max: 50,
                    message: 'Please enter 50 characters or fewer!',
                  },
                ]}
              >
                <Input placeholder="Type... " />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name="obj_name"
                label="Common Title"
                rules={[
                  {
                    max: 50,
                    message: 'Please enter 50 characters or fewer!',
                  },
                ]}
              >
                <Input placeholder="Type..." />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="artist" label="Artist">
                <Select
                  placeholder="Select Artist/ Creator"
                  optionFilterProp="children"
                  showSearch
                  allowClear
                  filterOption={(input, option) => option.children.includes(input)}
                >
                  {artists?.map(artist => {
                    return (
                      <Select.Option key={artist.id} value={artist.id}>
                        {artist.First_Name} {artist.last_name}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item>
                <Button
                  type="primary"
                  size="small"
                  htmlType="submit"
                  style={{
                    marginTop: '28px',
                    height: '42px',
                  }}
                  icon={<SearchOutlined />}
                >
                  Search
                </Button>
                <Button
                  style={{
                    marginLeft: '10px',
                    height: '42px',
                    marginTop: '28px',
                  }}
                  size="small"
                  danger
                  onClick={resetNegFilters}
                  icon={<ReloadOutlined />}
                >
                  Reset Filters
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <Row gutter={24}>
          <Col xs={24} sm={24} md={24} lg={24}>
            <hr />
            <div className="artist-list" id="negative_filter_table">
              <Table
                className="no-fixed-last-col"
                scroll={{ x: 'max-content' }}
                columns={negColumns}
                bordered
                rowKey={record => record.id}
                dataSource={filterNegData}
                loading={loading}
              />
            </div>
          </Col>
        </Row>
      </Modal>
    </>
  );
};
export default AdvanceNegativeSearch;
